import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactFlow, {
  MiniMap, Controls, Background, useNodesState, useEdgesState, addEdge, MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { processFlowsAPI, partsAPI, machinesAPI, bomAPI, costingsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Save, Trash2, Activity, FileDown, Loader2, AlertTriangle, 
  Package, Search, Building2, Truck, Calculator, Eye, Sparkles, X 
} from 'lucide-react';
import { PaginationControls } from '../components/PaginationControls';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/bulk/BulkActionBar';

const FLOW_CSV_COLUMNS = [
  { header: 'Part No', value: (row) => row.part_internal_no },
  { header: 'Description', value: (row) => row.part_description },
  { header: 'Steps', value: (row) => row.steps?.length || 0 },
  { header: 'Version', value: (row) => row.version_number },
  { header: 'Active', value: (row) => (row.is_active ? 'Yes' : 'No') },
  { header: 'Status', value: (row) => row.status },
];

// Custom node for process steps
const ProcessNode = ({ data }) => (
  <div className={`process-node ${data.isCritical ? 'critical border-secondary' : ''}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-muted-foreground">Step {data.stepNumber}</span>
      <div className="flex gap-1">
        {data.source === 'outsourced' && (
          <Badge className="bg-orange-100 text-orange-700 text-xs">Outsourced</Badge>
        )}
        {data.isCritical && <Badge className="badge-error text-xs">Critical</Badge>}
      </div>
    </div>
    <h4 className="font-semibold text-foreground">{data.processName}</h4>
    {data.source === 'in_house' && data.machineName && (
      <p className="text-sm text-muted-foreground mt-1">{data.machineName}</p>
    )}
    {data.source === 'outsourced' && data.vendorName && (
      <p className="text-sm text-orange-600 mt-1">Vendor: {data.vendorName}</p>
    )}
  </div>
);

const nodeTypes = { processNode: ProcessNode };

// Process Flows List Page
export default function ProcessFlowsPage() {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const [limit, setLimit] = useState(50);
  const selection = useBulkSelection(flows);

  useEffect(() => {
    setPage(1);
  }, [search, limit]);

  useEffect(() => {
    fetchFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, limit]);

  const fetchFlows = async () => {
    try {
      const params = { page, limit };
      if (search) params.search = search;
      const response = await processFlowsAPI.list(params);
      const items = response.data?.items || response.data?.data || [];
      setFlows(items);
      selection.remember(items);
      setTotal(response.data?.total || 0);
      setTotalPages(response.data?.total_pages || 1);
    } catch (error) {
      toast.error('Failed to fetch process flows');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">Process Flows</h1>
          <p className="text-muted-foreground">Manage manufacturing process flows for parts</p>
        </div>
        <Button className="btn-primary" onClick={() => navigate('/process-flows/new')}>
          <Plus className="mr-2 h-4 w-4" />New Process Flow
        </Button>
      </div>

      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by part no, description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="process-flow-search-input"
              />
            </div>
            <Badge variant="outline">{total} process flows</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <BulkActionBar
            entity="process_flows"
            selectedIds={selection.selectedIds}
            selectedRows={selection.selectedRows}
            onClear={selection.clear}
            onDone={fetchFlows}
            csvColumns={FLOW_CSV_COLUMNS}
            csvPrefix="process_flows"
          />
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : flows.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No process flows found</p>
              <p className="text-sm">Create a process flow for a part to get started</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selection.allOnPageSelected}
                        onCheckedChange={selection.togglePage}
                        aria-label="Select all process flows on this page"
                        data-testid="process-flows-select-all-page"
                      />
                    </TableHead>
                    <TableHead>Part No</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Steps</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flows.map((flow) => (
                    <TableRow key={flow.id} className="table-row" data-state={selection.isSelected(flow.id) ? 'selected' : undefined}>
                      <TableCell>
                        <Checkbox
                          checked={selection.isSelected(flow.id)}
                          onCheckedChange={() => selection.toggleOne(flow)}
                          aria-label={`Select process flow ${flow.part_internal_no}`}
                          data-testid={`process-flows-row-checkbox-${flow.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono font-medium">{flow.part_internal_no}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{flow.part_description}</TableCell>
                      <TableCell>{flow.steps?.length || 0} steps</TableCell>
                      <TableCell>V{flow.version_number}</TableCell>
                      <TableCell>
                        <Badge className={flow.is_active ? 'badge-success' : 'badge-warning'}>
                          {flow.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/process-flows/${flow.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          {!loading && flows.length > 0 && (
            <div className="mt-4">
              <PaginationControls
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onLimitChange={setLimit}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Process Flow Builder Page (Part-Centric)
export function ProcessFlowBuilderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partIdFromUrl = searchParams.get('part');
  const isSmartCopy = searchParams.get('smartCopy') === 'true';
  const flowId = window.location.pathname.includes('/process-flows/') && 
                 !window.location.pathname.includes('/new') 
                 ? window.location.pathname.split('/')[2] : null;
  
  const [part, setPart] = useState(null);
  const [partSearch, setPartSearch] = useState('');
  const [partResults, setPartResults] = useState([]);
  const [searchingParts, setSearchingParts] = useState(false);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Smart Copy prefill state
  const [smartCopySource, setSmartCopySource] = useState(null);
  const [showSmartCopyBanner, setShowSmartCopyBanner] = useState(false);
  
  // Copy From Existing state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyFlowSearch, setCopyFlowSearch] = useState('');
  const [copyFlowResults, setCopyFlowResults] = useState([]);
  const [searchingFlows, setSearchingFlows] = useState(false);
  const [copiedFromSource, setCopiedFromSource] = useState(null);
  
  const [steps, setSteps] = useState([]);
  const [editingStep, setEditingStep] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({
      ...params, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed },
    }, eds)),
    [setEdges]
  );

  // Smart Copy: Load prefill data from sessionStorage
  useEffect(() => {
    if (isSmartCopy && partIdFromUrl) {
      const prefillDataStr = sessionStorage.getItem('smartCopyPrefill');
      if (prefillDataStr) {
        try {
          const prefillData = JSON.parse(prefillDataStr);
          // Only use if partId matches
          if (prefillData.partId === partIdFromUrl) {
            setSmartCopySource({
              sourcePartNo: prefillData.sourcePartNo,
              sourceFlowId: prefillData.sourceFlowId
            });
            setShowSmartCopyBanner(true);
            
            // Pre-fill the steps from the source flow
            if (prefillData.steps && prefillData.steps.length > 0) {
              // Generate new IDs for the copied steps
              const copiedSteps = prefillData.steps.map((step, idx) => ({
                ...step,
                id: `step-${Date.now()}-${idx}`,
                step_number: idx + 1
              }));
              setSteps(copiedSteps);
              toast.success(`Process flow pre-filled from ${prefillData.sourcePartNo}. Review and click Save to confirm.`);
            }
          }
          // Clear the sessionStorage after reading
          sessionStorage.removeItem('smartCopyPrefill');
        } catch (e) {
        }
      }
    }
  }, [isSmartCopy, partIdFromUrl]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partIdFromUrl, flowId]);

  useEffect(() => {
    updateNodesFromSteps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  // Part search effect
  useEffect(() => {
    if (partSearch.length >= 2 && !part) {
      searchParts();
    } else {
      setPartResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partSearch]);

  // Copy Flow search effect
  useEffect(() => {
    if (copyFlowSearch.length >= 2) {
      searchFlowsToCopy();
    } else {
      setCopyFlowResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyFlowSearch]);

  const searchParts = async () => {
    setSearchingParts(true);
    try {
      const res = await partsAPI.search(partSearch);
      // Filter to show only Components (not Kits - Kits use BOM)
      setPartResults((res.data?.items || res.data || []).filter(p => p.part_category !== 'kit'));
    } catch (e) {
    } finally {
      setSearchingParts(false);
    }
  };

  // Search for existing flows to copy from
  const searchFlowsToCopy = async () => {
    setSearchingFlows(true);
    try {
      const res = await processFlowsAPI.list({ search: copyFlowSearch, is_active: true, limit: 10 });
      setCopyFlowResults(res.data?.items || res.data?.data || []);
    } catch (e) {
    } finally {
      setSearchingFlows(false);
    }
  };

  // Copy steps from an existing flow
  const copyFromExistingFlow = async (sourceFlow) => {
    try {
      // Fetch full flow details to get all steps
      const flowRes = await processFlowsAPI.get(sourceFlow.id);
      const sourceSteps = flowRes.data?.steps || [];
      
      if (sourceSteps.length === 0) {
        toast.error('Selected flow has no steps to copy');
        return;
      }
      
      // Generate new IDs for the copied steps
      const copiedSteps = sourceSteps.map((step, idx) => ({
        ...step,
        id: `step-copy-${Date.now()}-${idx}`,
        step_number: idx + 1
      }));
      
      setSteps(copiedSteps);
      setCopiedFromSource({
        partNo: sourceFlow.part_internal_no,
        flowId: sourceFlow.id
      });
      setCopyDialogOpen(false);
      setCopyFlowSearch('');
      setCopyFlowResults([]);
      setShowSmartCopyBanner(false); // Hide AI banner if visible
      
      toast.success(`Copied ${copiedSteps.length} steps from ${sourceFlow.part_internal_no}. Review and modify as needed.`);
    } catch (e) {
      toast.error('Failed to copy flow');
    }
  };

  const fetchData = async () => {
    try {
      const machinesRes = await machinesAPI.list({ is_active: true });
      setMachines(machinesRes.data);

      if (flowId) {
        const flowRes = await processFlowsAPI.get(flowId);
        setSteps(flowRes.data.steps || []);
        // Fetch part details
        const partRes = await partsAPI.get(flowRes.data.part_id);
        setPart(partRes.data.part || partRes.data);
      } else if (partIdFromUrl) {
        const partRes = await partsAPI.get(partIdFromUrl);
        setPart(partRes.data.part || partRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const selectPart = (selectedPart) => {
    setPart(selectedPart);
    setPartSearch('');
    setPartResults([]);
  };

  const updateNodesFromSteps = () => {
    const newNodes = steps.map((step, index) => ({
      id: step.id,
      type: 'processNode',
      position: { x: 250, y: index * 150 },
      data: {
        stepNumber: index + 1,
        processName: step.process_name,
        machineName: step.machine_name,
        vendorName: step.vendor_name,
        source: step.source || 'in_house',
        isCritical: step.is_critical,
        costBasis: step.cost_basis,
      },
    }));
    setNodes(newNodes);
    
    const newEdges = steps.slice(0, -1).map((step, index) => ({
      id: `e${step.id}-${steps[index + 1].id}`,
      source: step.id,
      target: steps[index + 1].id,
      type: 'smoothstep',
      markerEnd: { type: MarkerType.ArrowClosed },
    }));
    setEdges(newEdges);
  };

  const addStep = () => {
    setEditingStep({
      id: `step_${Date.now()}`,
      process_name: '',
      source: 'in_house',
      machine_id: '',
      machine_name: '',
      vendor_name: '',
      vendor_rate: '',
      is_critical: false,
      cost_basis: 'hour_based',
      output_per_hour: '',
      hourly_rate: '',
      oee_percentage: 85,
      rate_per_kg: '',
      rate_per_piece: '',
      setup_time_minutes: '',
      tool_cost: '',
      tool_life_pieces: '',
    });
    setDialogOpen(true);
  };

  const editStep = (step) => {
    setEditingStep({ ...step });
    setDialogOpen(true);
  };

  const deleteStep = (stepId) => {
    setSteps(prev => prev.filter(s => s.id !== stepId));
  };

  const saveStep = (stepData) => {
    setSteps(prev => {
      const existing = prev.find(s => s.id === stepData.id);
      if (existing) {
        return prev.map(s => s.id === stepData.id ? stepData : s);
      }
      return [...prev, stepData];
    });
    setDialogOpen(false);
    setEditingStep(null);
  };

  const saveFlow = async () => {
    if (!part || steps.length === 0) {
      toast.error('Please select a part and add at least one step');
      return;
    }

    setSaving(true);
    try {
      const data = {
        part_id: part.id,
        steps: steps.map((s, i) => ({
          ...s,
          step_number: i + 1,
          source: s.source || 'in_house',
          output_per_hour: s.output_per_hour ? parseFloat(s.output_per_hour) : null,
          hourly_rate: s.hourly_rate ? parseFloat(s.hourly_rate) : null,
          oee_percentage: s.oee_percentage ? parseFloat(s.oee_percentage) : 85,
          rate_per_kg: s.rate_per_kg ? parseFloat(s.rate_per_kg) : null,
          rate_per_piece: s.rate_per_piece ? parseFloat(s.rate_per_piece) : null,
          vendor_rate: s.vendor_rate ? parseFloat(s.vendor_rate) : null,
          setup_time_minutes: s.setup_time_minutes ? parseFloat(s.setup_time_minutes) : null,
          tool_cost: s.tool_cost ? parseFloat(s.tool_cost) : null,
          tool_life_pieces: s.tool_life_pieces ? parseFloat(s.tool_life_pieces) : null,
        })),
      };

      if (flowId) {
        await processFlowsAPI.update(flowId, data);
        toast.success('Process flow updated');
      } else {
        const response = await processFlowsAPI.create(data);
        toast.success('Process flow created');
        navigate(`/process-flows/${response.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!flowId) {
      toast.error('Please save the flow first');
      return;
    }
    // Open print view in new tab - browser will handle PDF generation
    window.open(`/process-flows/${flowId}/print`, '_blank');
    toast.success('Print view opened in new tab');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Smart Copy Banner */}
      {showSmartCopyBanner && smartCopySource && (
        <div className="flex items-center justify-between gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">
                Smart Copy: Process flow pre-filled from {smartCopySource.sourcePartNo}
              </p>
              <p className="text-sm text-blue-600">
                Review the steps below and click "Save Flow" to commit this process flow to the database.
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowSmartCopyBanner(false)}
            className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
            {flowId ? 'Edit Process Flow' : 'New Process Flow'}
          </h1>
          {part && (
            <p className="text-muted-foreground">
              Part: {part.internal_part_no} - {part.description}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/process-flows')}>Cancel</Button>
          {flowId && (
            <Button variant="outline" onClick={handleDownloadPdf}>
              <FileDown className="mr-2 h-4 w-4" />
              Print / Save PDF
            </Button>
          )}
          <Button className="btn-primary" onClick={saveFlow} disabled={saving || !part}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : (showSmartCopyBanner ? 'Save Flow' : 'Save Flow')}
          </Button>
        </div>
      </div>

      {/* Part Selection */}
      {!part && !flowId && (
        <Card className="card-dashboard border-amber-500/50 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="text-lg font-heading uppercase flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Select a Component Part *
            </CardTitle>
            <CardDescription>Search and select a Component part to build its process flow (Kits use BOM instead)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by part number or description..."
                value={partSearch}
                onChange={(e) => setPartSearch(e.target.value)}
                className="pl-9"
              />
              {searchingParts && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
            </div>
            {partResults.length > 0 && (
              <div className="mt-2 border rounded-lg max-h-60 overflow-y-auto">
                {partResults.map((p) => (
                  <div
                    key={p.id}
                    className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0"
                    onClick={() => selectPart(p)}
                  >
                    <div className="flex justify-between">
                      <span className="font-mono font-medium">{p.internal_part_no}</span>
                      <Badge variant="outline">{p.part_type}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{p.description}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selected Part Info */}
      {part && (
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Package className="h-10 w-10 text-primary" />
              <div>
                <p className="font-mono font-bold text-lg">{part.internal_part_no}</p>
                <p className="text-muted-foreground">{part.description}</p>
                <div className="flex gap-2 mt-1">
                  <Badge variant="outline">{part.part_type}</Badge>
                  {part.grade && <Badge variant="outline">{part.grade}</Badge>}
                  <Badge className={part.manufacturing_type === 'outsourced' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}>
                    {part.manufacturing_type === 'outsourced' ? 'Outsourced' : 'In-House'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Process Steps */}
      {part && (
        <>
          {/* Copied From Banner */}
          {copiedFromSource && (
            <div className="flex items-center justify-between gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">
                    Process flow copied from {copiedFromSource.partNo}
                  </p>
                  <p className="text-sm text-green-600">
                    Review the steps below and modify as needed. Click "Save Flow" to save.
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setCopiedFromSource(null)}
                className="text-green-600 hover:text-green-800 hover:bg-green-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading uppercase">Process Steps</CardTitle>
              <div className="flex gap-2">
                {!flowId && steps.length === 0 && (
                  <Button 
                    variant="outline" 
                    onClick={() => setCopyDialogOpen(true)}
                    data-testid="copy-from-existing-btn"
                  >
                    <Activity className="mr-2 h-4 w-4" />
                    Copy From Existing
                  </Button>
                )}
                <Button onClick={addStep} data-testid="add-step-btn">
                  <Plus className="mr-2 h-4 w-4" />Add Step
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {steps.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No process steps added yet</p>
                  <p className="text-sm">Click &quot;Add Step&quot; to start building the process flow</p>
                  <p className="text-sm text-primary mt-2">or use &quot;Copy From Existing&quot; to clone from another part</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {steps.map((step, index) => (
                    <div key={step.id} className="flex items-center justify-between p-4 border rounded-lg hover:border-primary/30 transition-colors">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold text-muted-foreground font-mono">{index + 1}</span>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{step.process_name}</h4>
                            {step.source === 'outsourced' && <Badge className="bg-orange-100 text-orange-700">Outsourced</Badge>}
                            {step.is_critical && <Badge className="badge-error">Critical</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {step.source === 'in_house' ? (step.machine_name || 'No machine') : `Vendor: ${step.vendor_name || 'TBD'}`}
                            {' • '}{step.cost_basis?.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => editStep(step)}>Edit</Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteStep(step.id)} className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Visual Flow */}
          {steps.length > 0 && (
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-lg font-heading uppercase">Visual Flow</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] border rounded-lg bg-background">
                  <ReactFlow
                    nodes={nodes} edges={edges}
                    onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                    onConnect={onConnect} nodeTypes={nodeTypes} fitView
                  >
                    <Controls /><MiniMap /><Background variant="dots" gap={12} size={1} />
                  </ReactFlow>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Step Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">
              {editingStep?.process_name ? 'Edit Step' : 'Add Process Step'}
            </DialogTitle>
          </DialogHeader>
          {editingStep && (
            <StepForm step={editingStep} machines={machines} onSave={saveStep} onCancel={() => { setDialogOpen(false); setEditingStep(null); }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Copy From Existing Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Copy From Existing Process Flow
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Search for an existing part's process flow and copy its steps to use as a starting point.
            </p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by part number or description..."
                value={copyFlowSearch}
                onChange={(e) => setCopyFlowSearch(e.target.value)}
                className="pl-9"
                data-testid="copy-flow-search-input"
              />
              {searchingFlows && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              )}
            </div>
            {copyFlowResults.length > 0 && (
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {copyFlowResults.map((flow) => (
                  <div
                    key={flow.id}
                    className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0"
                    onClick={() => copyFromExistingFlow(flow)}
                    data-testid={`copy-flow-${flow.part_internal_no}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-mono font-medium">{flow.part_internal_no}</span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {flow.steps?.length || 0} steps
                        </Badge>
                      </div>
                      <Badge className={flow.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                        V{flow.version_number}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{flow.part_description}</p>
                  </div>
                ))}
              </div>
            )}
            {copyFlowSearch.length >= 2 && copyFlowResults.length === 0 && !searchingFlows && (
              <div className="text-center py-4 text-muted-foreground">
                <p>No process flows found</p>
                <p className="text-xs">Try a different search term</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Step Form with In-House/Outsourced toggle
function StepForm({ step, machines, onSave, onCancel }) {
  const [formData, setFormData] = useState({ ...step, source: step.source || 'in_house' });

  const handleMachineChange = (machineId) => {
    const machine = machines.find(m => m.id === machineId);
    setFormData(prev => ({
      ...prev,
      machine_id: machineId,
      machine_name: machine?.name || '',
      hourly_rate: machine?.hourly_rate || prev.hourly_rate,
      oee_percentage: machine?.oee_percentage || prev.oee_percentage,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Source Toggle - NEW */}
      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
        <Label className="mb-3 block">Production Source *</Label>
        <div className="flex gap-4">
          <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.source === 'in_house' ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/30'}`}>
            <input type="radio" name="source" value="in_house" checked={formData.source === 'in_house'} onChange={() => setFormData(p => ({ ...p, source: 'in_house', vendor_name: '', vendor_rate: '' }))} className="sr-only" />
            <Building2 className={`h-5 w-5 ${formData.source === 'in_house' ? 'text-primary' : 'text-muted-foreground'}`} />
            <div>
              <p className="font-medium">In-House</p>
              <p className="text-xs text-muted-foreground">Use internal machines</p>
            </div>
          </label>
          <label className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${formData.source === 'outsourced' ? 'border-orange-500 bg-orange-50' : 'border-border hover:border-orange-300'}`}>
            <input type="radio" name="source" value="outsourced" checked={formData.source === 'outsourced'} onChange={() => setFormData(p => ({ ...p, source: 'outsourced', machine_id: '', machine_name: '' }))} className="sr-only" />
            <Truck className={`h-5 w-5 ${formData.source === 'outsourced' ? 'text-orange-600' : 'text-muted-foreground'}`} />
            <div>
              <p className="font-medium">Outsourced</p>
              <p className="text-xs text-muted-foreground">External vendor</p>
            </div>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Process Name *</Label>
          <Input value={formData.process_name} onChange={(e) => setFormData(prev => ({ ...prev, process_name: e.target.value }))} placeholder="e.g., Cold Forging" required />
        </div>
        
        {/* Conditional: Machine (In-House) or Vendor (Outsourced) */}
        {formData.source === 'in_house' ? (
          <div className="space-y-2">
            <Label>Machine</Label>
            <Select value={formData.machine_id} onValueChange={handleMachineChange}>
              <SelectTrigger><SelectValue placeholder="Select machine" /></SelectTrigger>
              <SelectContent>
                {machines.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name} ({m.process_type})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="space-y-2">
            <Label>Vendor Name *</Label>
            <Input value={formData.vendor_name || ''} onChange={(e) => setFormData(prev => ({ ...prev, vendor_name: e.target.value }))} placeholder="e.g., ABC Plating Co." required={formData.source === 'outsourced'} />
          </div>
        )}
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox id="is_critical" checked={formData.is_critical} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_critical: checked }))} />
        <Label htmlFor="is_critical">Mark as Critical Process</Label>
      </div>

      <div className="space-y-2">
        <Label>Cost Basis *</Label>
        <Select value={formData.cost_basis} onValueChange={(value) => setFormData(prev => ({ ...prev, cost_basis: value }))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hour_based">Hour Based (₹/hour × time)</SelectItem>
            <SelectItem value="per_kg">Per Kilogram (₹/kg)</SelectItem>
            <SelectItem value="per_piece">Per Piece (₹/piece)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cost inputs based on source and cost_basis */}
      {formData.source === 'in_house' && formData.cost_basis === 'hour_based' && (
        <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
          <div className="space-y-2">
            <Label>Output (Pcs/Minute) *</Label>
            <Input type="number" value={formData.output_per_hour} onChange={(e) => setFormData(prev => ({ ...prev, output_per_hour: e.target.value }))} placeholder="e.g., 60 or 100" />
            <p className="text-xs text-muted-foreground">Machine pieces per minute</p>
          </div>
          <div className="space-y-2">
            <Label>Hourly Rate (₹) *</Label>
            <Input type="number" value={formData.hourly_rate} onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label>OEE %</Label>
            <Input type="number" value={formData.oee_percentage} onChange={(e) => setFormData(prev => ({ ...prev, oee_percentage: e.target.value }))} />
          </div>
        </div>
      )}

      {formData.source === 'outsourced' && (
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-3">
          <div>
            <Label className="text-orange-700">
              Vendor Rate (₹/{formData.cost_basis === 'per_kg' ? 'kg' : 'pc'}) *
            </Label>
            <Input 
              type="number" 
              step="0.01"
              value={formData.vendor_rate || ''} 
              onChange={(e) => setFormData(prev => ({ ...prev, vendor_rate: e.target.value }))} 
              placeholder={formData.cost_basis === 'per_kg' ? 'Rate per kg (e.g., 50)' : 'Rate per piece (e.g., 0.50)'} 
              className="mt-2" 
            />
            {formData.cost_basis === 'per_kg' && (
              <p className="text-xs text-orange-600 mt-1">
                Cost will be calculated: (Net Weight × Rate/Kg) / 1000
              </p>
            )}
          </div>
        </div>
      )}

      {formData.source === 'in_house' && formData.cost_basis === 'per_kg' && (
        <div className="p-4 bg-muted/30 rounded-lg">
          <Label>Rate per Kg (₹) *</Label>
          <Input type="number" value={formData.rate_per_kg} onChange={(e) => setFormData(prev => ({ ...prev, rate_per_kg: e.target.value }))} />
        </div>
      )}

      {formData.source === 'in_house' && formData.cost_basis === 'per_piece' && (
        <div className="p-4 bg-muted/30 rounded-lg">
          <Label>Rate per Piece (₹) *</Label>
          <Input type="number" value={formData.rate_per_piece} onChange={(e) => setFormData(prev => ({ ...prev, rate_per_piece: e.target.value }))} />
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Setup Time (min)</Label>
          <Input type="number" value={formData.setup_time_minutes} onChange={(e) => setFormData(prev => ({ ...prev, setup_time_minutes: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>Tool Cost (₹)</Label>
          <Input type="number" value={formData.tool_cost} onChange={(e) => setFormData(prev => ({ ...prev, tool_cost: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <Label>Tool Life (pcs)</Label>
          <Input type="number" value={formData.tool_life_pieces} onChange={(e) => setFormData(prev => ({ ...prev, tool_life_pieces: e.target.value }))} />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="btn-primary">Save Step</Button>
      </div>
    </form>
  );
}

// BOM Builder Page (for Kits)
export function BOMBuilderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partIdFromUrl = searchParams.get('part');
  const bomId = window.location.pathname.includes('/bom/') && !window.location.pathname.includes('/new') 
    ? window.location.pathname.split('/bom/')[1] : null;
  
  const [part, setPart] = useState(null);
  const [partSearch, setPartSearch] = useState('');
  const [partResults, setPartResults] = useState([]);
  const [childSearch, setChildSearch] = useState('');
  const [childResults, setChildResults] = useState([]);
  const [bomItems, setBomItems] = useState([]);
  const [assemblyLaborCost, setAssemblyLaborCost] = useState(0);
  const [packagingCost, setPackagingCost] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [priceChanged, setPriceChanged] = useState(false);
  const [loadedBomId, setLoadedBomId] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partIdFromUrl, bomId]);

  useEffect(() => {
    if (partSearch.length >= 2 && !part) {
      searchKitParts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partSearch]);

  useEffect(() => {
    if (childSearch.length >= 2) {
      searchChildParts();
    } else {
      setChildResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childSearch]);

  const searchKitParts = async () => {
    try {
      const res = await partsAPI.search(partSearch);
      // Only show Kit parts
      setPartResults((res.data?.items || res.data || []).filter(p => p.part_category === 'kit'));
    } catch (e) { }
  };

  const searchChildParts = async () => {
    try {
      const res = await partsAPI.search(childSearch);
      // Only show Component parts (not Kits) as children
      setChildResults((res.data?.items || res.data || []).filter(p => p.part_category !== 'kit'));
    } catch (e) { }
  };

  const fetchData = async () => {
    try {
      if (bomId) {
        const bomRes = await bomAPI.get(bomId);
        applyBom(bomRes.data);
        const partRes = await partsAPI.get(bomRes.data.part_id);
        setPart(partRes.data.part || partRes.data);
      } else if (partIdFromUrl) {
        const partRes = await partsAPI.get(partIdFromUrl);
        setPart(partRes.data.part || partRes.data);
        // Load existing BOM if any
        const bomRes = await bomAPI.getByPart(partIdFromUrl);
        if (bomRes.data?.items?.length > 0) {
          const detail = await bomAPI.get(bomRes.data.items[0].id);
          applyBom(detail.data);
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const applyBom = (data) => {
    setLoadedBomId(data.id);
    setBomItems(data.items || []);
    setAssemblyLaborCost(data.assembly_labor_cost || 0);
    setPackagingCost(data.packaging_cost || 0);
    setPricing(data.pricing || null);
    setPriceChanged(Boolean(data.price_changed_since_accepted));
  };

  const activeBomId = bomId || loadedBomId;

  const reloadPricing = async (id) => {
    try {
      const detail = await bomAPI.get(id);
      applyBom(detail.data);
    } catch { /* keep the local edits visible */ }
  };

  const acceptPrice = async () => {
    setAccepting(true);
    try {
      await bomAPI.acceptPrice(activeBomId);
      toast.success('Kit price accepted from this BOM');
      await reloadPricing(activeBomId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not accept the kit price');
    } finally { setAccepting(false); }
  };

  const selectPart = (selectedPart) => {
    setPart(selectedPart);
    setPartSearch('');
    setPartResults([]);
  };

  const addChildPart = (childPart) => {
    if (bomItems.find(i => i.child_part_id === childPart.id)) {
      toast.error('Part already added to BOM');
      return;
    }
    setBomItems(prev => [...prev, {
      id: `bom_${Date.now()}`,
      child_part_id: childPart.id,
      child_part_no: childPart.internal_part_no,
      child_description: childPart.description,
      quantity_per_kit: 1,
      unit: 'pcs',
      child_costing_id: childPart.active_costing_id,
      child_cost_per_piece: null, // Will be fetched
    }]);
    setChildSearch('');
    setChildResults([]);
  };

  const updateItemQty = (itemId, qty) => {
    setBomItems(prev => prev.map(i => i.id === itemId ? { ...i, quantity_per_kit: parseFloat(qty) || 0 } : i));
  };

  const removeItem = (itemId) => {
    setBomItems(prev => prev.filter(i => i.id !== itemId));
  };

  const calculateRollupCost = () => {
    const componentsCost = bomItems.reduce((sum, item) => {
      return sum + (item.quantity_per_kit * (item.child_cost_per_piece || 0));
    }, 0);
    return componentsCost + assemblyLaborCost + packagingCost;
  };

  const saveBOM = async () => {
    if (!part || bomItems.length === 0) {
      toast.error('Please select a Kit part and add at least one child part');
      return;
    }

    setSaving(true);
    try {
      const data = {
        part_id: part.id,
        items: bomItems.map(i => ({
          child_part_id: i.child_part_id,
          quantity_per_kit: i.quantity_per_kit,
          unit: i.unit,
        })),
        assembly_labor_cost: assemblyLaborCost,
        packaging_cost: packagingCost,
      };

      if (activeBomId) {
        await bomAPI.update(activeBomId, data);
        toast.success('BOM updated');
        await reloadPricing(activeBomId);
      } else {
        const res = await bomAPI.create(data);
        toast.success('BOM created');
        navigate(`/bom/${res.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save BOM');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
            {bomId ? 'Edit BOM' : 'New Bill of Materials'}
          </h1>
          {part && <p className="text-muted-foreground">Kit: {part.internal_part_no} - {part.description}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/parts')}>Cancel</Button>
          <Button className="btn-primary" onClick={saveBOM} disabled={saving || !part}>
            <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save BOM'}
          </Button>
        </div>
      </div>

      {/* Kit Part Selection */}
      {!part && (
        <Card className="card-dashboard border-purple-500/50 bg-purple-500/5">
          <CardHeader>
            <CardTitle className="text-lg font-heading uppercase flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-500" />Select a Kit Part *
            </CardTitle>
            <CardDescription>Search for a Kit part to define its Bill of Materials</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search Kit parts..." value={partSearch} onChange={(e) => setPartSearch(e.target.value)} className="pl-9" />
            </div>
            {partResults.length > 0 && (
              <div className="mt-2 border rounded-lg max-h-60 overflow-y-auto">
                {partResults.map((p) => (
                  <div key={p.id} className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0" onClick={() => selectPart(p)}>
                    <div className="flex justify-between">
                      <span className="font-mono font-medium">{p.internal_part_no}</span>
                      <Badge className="bg-purple-100 text-purple-700">Kit</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{p.description}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {part && (
        <>
          {/* Selected Kit Info */}
          <Card className="card-dashboard bg-purple-50 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Package className="h-10 w-10 text-purple-600" />
                <div>
                  <p className="font-mono font-bold text-lg">{part.internal_part_no}</p>
                  <p className="text-muted-foreground">{part.description}</p>
                  <Badge className="mt-1 bg-purple-100 text-purple-700">Kit / Assembly</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* BOM Items Table */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Child Parts (Components)</CardTitle>
              <CardDescription>Add component parts that make up this kit</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search Child Parts */}
              <div className="mb-4 relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search components to add..." value={childSearch} onChange={(e) => setChildSearch(e.target.value)} className="pl-9" />
                {childResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 border rounded-lg bg-card max-h-60 overflow-y-auto shadow-lg">
                    {childResults.map((p) => (
                      <div key={p.id} className="p-3 hover:bg-muted/50 cursor-pointer border-b last:border-0" onClick={() => addChildPart(p)}>
                        <div className="flex justify-between">
                          <span className="font-mono font-medium">{p.internal_part_no}</span>
                          <Badge variant="outline">{p.part_type}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{p.description}</p>
                        {p.active_costing_id && <p className="text-xs text-green-600">Has Active Costing</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* BOM Items Table */}
              {bomItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No child parts added</p>
                  <p className="text-sm">Search and add components above</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Part No</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Qty/Kit</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Unit Cost</TableHead>
                        <TableHead>Line Cost</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bomItems.map((item) => (
                        <TableRow key={item.id} className="table-row">
                          <TableCell className="font-mono font-medium">{item.child_part_no}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{item.child_description}</TableCell>
                          <TableCell>
                            <Input type="number" value={item.quantity_per_kit} onChange={(e) => updateItemQty(item.id, e.target.value)} className="w-20" min="0.01" step="0.01" />
                          </TableCell>
                          <TableCell>
                            <Select value={item.unit} onValueChange={(v) => setBomItems(prev => prev.map(i => i.id === item.id ? { ...i, unit: v } : i))}>
                              <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pcs">Pcs</SelectItem>
                                <SelectItem value="kg">Kg</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="font-mono">
                            {item.child_cost_per_piece != null ? `₹${item.child_cost_per_piece.toFixed(4)}` : <span className="text-amber-600">No costing</span>}
                          </TableCell>
                          <TableCell className="font-mono font-medium">
                            ₹{((item.quantity_per_kit || 0) * (item.child_cost_per_piece || 0)).toFixed(4)}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)} className="text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Kit price — the BOM is the single source */}
          <Card className="card-dashboard" data-testid="bom-kit-price-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase flex items-center gap-2">
                <Calculator className="h-5 w-5" />Kit price
              </CardTitle>
              <CardDescription>
                Child parts already carry their own margin, so kitting adds only labour and packaging.
                No extra margin is applied on a kit. This price is what costing and quotations use.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Kitting labour (&#8377;/kit)</Label>
                  <Input type="number" value={assemblyLaborCost} data-testid="bom-kitting-labour-input"
                    onChange={(e) => setAssemblyLaborCost(parseFloat(e.target.value) || 0)} />
                </div>
                <div className="space-y-2">
                  <Label>Kitting / packaging (&#8377;/kit)</Label>
                  <Input type="number" value={packagingCost} data-testid="bom-kitting-packaging-input"
                    onChange={(e) => setPackagingCost(parseFloat(e.target.value) || 0)} />
                </div>
                <div className="rounded-lg bg-primary/10 p-4">
                  <Label className="text-muted-foreground">Kit price / kit</Label>
                  <p className="font-mono text-3xl font-bold text-primary" data-testid="bom-kit-price">
                    {pricing?.is_priceable ? `\u20b9${pricing.kit_cost_per_kit.toFixed(4)}` : 'Blocked'}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Components: &#8377;{(pricing?.components_cost_per_kit ?? calculateRollupCost() - assemblyLaborCost - packagingCost).toFixed(4)}
                    {' '}+ Labour: &#8377;{assemblyLaborCost} + Packaging: &#8377;{packagingCost}
                  </p>
                  {pricing?.kit_weight_kg && (
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="bom-kit-weight">
                      Internal reference: {pricing.kit_weight_kg.toFixed(4)} kg / kit
                      {pricing.price_per_kg_reference ? ` \u00b7 \u20b9${pricing.price_per_kg_reference.toFixed(4)} / kg` : ''}
                    </p>
                  )}
                </div>
              </div>

              {pricing && !pricing.is_priceable && (
                <div className="border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="bom-price-blocked">
                  <div className="flex items-center gap-2 font-semibold">
                    <AlertTriangle className="h-4 w-4" /> The kit price is blocked until every child part has an approved costing
                  </div>
                  <ul className="mt-2 list-disc pl-6">
                    {pricing.missing_children.map((child) => (
                      <li key={child.child_part_id} data-testid={`bom-missing-child-${child.child_part_id}`}>
                        <span className="font-mono">{child.child_part_no || child.child_description}</span> — {child.reason}
                      </li>
                    ))}
                    {pricing.lines.length === 0 && <li>Add the child parts to this BOM first.</li>}
                  </ul>
                </div>
              )}

              {priceChanged && pricing?.is_priceable && (
                <div className="flex flex-col gap-2 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between"
                  data-testid="bom-price-changed-banner">
                  <span>A child cost or the kitting cost has changed since this kit price was accepted. Nothing was changed automatically.</span>
                  <Button size="sm" onClick={acceptPrice} disabled={accepting} data-testid="bom-accept-price-button">
                    {accepting ? 'Accepting...' : 'Accept new kit price'}
                  </Button>
                </div>
              )}

              {activeBomId && (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => window.open(`/bom/${activeBomId}/print`, '_blank')}
                    data-testid="bom-print-cost-sheet-button">
                    Print kit cost sheet
                  </Button>
                  {!priceChanged && pricing?.is_priceable && (
                    <Button type="button" variant="ghost" onClick={acceptPrice} disabled={accepting}
                      data-testid="bom-reaccept-price-button">Re-accept current price</Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
